import React, { FC, useState, useCallback, useMemo } from 'react'
import { UseFormMethods } from 'react-hook-form'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { Card } from 'transfix-ui/components/Card'
import { Grid } from 'transfix-ui/core'
import { canadianStatesOptions, countriesOptions, usStatesOptions } from 'constants/countriesStates'
import { getErrorMessage, getHQErrorMessage } from '../schema'
import { useContactInfoFormSectionStyles } from './useContactInfoFormSection'

const COUNTRY_OPTIONS = countriesOptions
const CA_STATE_OPTIONS = canadianStatesOptions
const US_STATE_OPTIONS = usStatesOptions

export interface IContactInfoFormSectionProps extends Pick<UseFormMethods, 'control' | 'errors'> {}

const ContactInfoFormSection: FC<IContactInfoFormSectionProps> = ({ control, errors }) => {
  const classes = useContactInfoFormSectionStyles()

  const [country, setCountry] = useState<'US' | 'CA'>('US')

  const stateOptions = useMemo(() => {
    return country === 'US' ? US_STATE_OPTIONS : CA_STATE_OPTIONS
  }, [country])

  const handleCountryChange = useCallback(({ target }) => {
    setCountry(target.value)
  }, [])

  return (
    <Card title='Contact Information'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ControlledTextInput
            control={control}
            name='headquarter.address'
            TextInputProps={{
              label: 'Billing Address',
              dataTest: 'billing-address-field',
              error: Boolean(getHQErrorMessage(errors, 'address')),
              validationMessage: getHQErrorMessage(errors, 'address'),
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ControlledSelect
            control={control}
            name='headquarter.countryCode'
            onChange={handleCountryChange}
            SelectProps={{
              label: 'Country',
              options: COUNTRY_OPTIONS,
              error: Boolean(getHQErrorMessage(errors, 'countryCode')),
              validationMessage: getHQErrorMessage(errors, 'countryCode'),
              placeholder: 'Select Country',
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ControlledTextInput
            control={control}
            name='headquarter.city'
            TextInputProps={{
              label: 'City',
              dataTest: 'city-field',
              error: Boolean(getHQErrorMessage(errors, 'city')),
              validationMessage: getHQErrorMessage(errors, 'city'),
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ControlledSelect
            control={control}
            name='headquarter.stateCode'
            SelectProps={{
              label: 'State',
              dataTest: 'state-field',
              options: stateOptions,
              error: Boolean(getHQErrorMessage(errors, 'stateCode')),
              validationMessage: getHQErrorMessage(errors, 'stateCode'),
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ControlledTextInput
            control={control}
            name='headquarter.zipcode'
            TextInputProps={{
              label: 'Postal Code',
              dataTest: 'postal-code-field',
              error: Boolean(getHQErrorMessage(errors, 'zipcode')),
              validationMessage: getHQErrorMessage(errors, 'zipcode'),
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <div>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <ControlledPhoneInput
                  control={control}
                  name='headquarter.contactPhone'
                  PhoneInputProps={{
                    label: 'Phone',
                    dataTest: 'phone-field',
                    invalid: Boolean(getHQErrorMessage(errors, 'contactPhone')),
                    validationMessage: getHQErrorMessage(errors, 'contactPhone'),
                    className: classes.phone,
                    required: true,
                    clearable: false,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextInput
                  control={control}
                  name='headquarter.contactPhoneExt'
                  TextInputProps={{
                    label: 'Ext',
                    error: Boolean(getHQErrorMessage(errors, 'contactPhoneExt')),
                    validationMessage: getHQErrorMessage(errors, 'contactPhoneExt'),
                    type: 'number',
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12} lg={3}>
          <ControlledTextInput
            control={control}
            name='contactEmail'
            TextInputProps={{
              label: 'Carrier Contact Email',
              error: Boolean(getErrorMessage(errors, 'contactEmail')),
              validationMessage: getErrorMessage(errors, 'contactEmail'),
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ControlledTextInput
            control={control}
            name='accountingEmail'
            TextInputProps={{
              label: 'Accounting Email #1',
              error: Boolean(getErrorMessage(errors, 'accountingEmail')),
              validationMessage: getErrorMessage(errors, 'accountingEmail'),
            }}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <ControlledTextInput
            control={control}
            name='secondaryAccountingEmail'
            TextInputProps={{
              label: 'Accounting Email #2',
              error: Boolean(getErrorMessage(errors, 'secondaryAccountingEmail')),
              validationMessage: getErrorMessage(errors, 'secondaryAccountingEmail'),
            }}
          />
        </Grid>
      </Grid>
    </Card>
  )
}

export default ContactInfoFormSection
